import { io, type Socket } from 'socket.io-client'
import { watch } from 'vue'
import config from '@/config'
import { useWorkspaceStore } from '@/stores/workspaces'
import { useSnackBarStore } from '@/stores/snack-bar'
import useTasks from '@/composables/useTasks'
import type { Task, TaskStatus } from '@/types/workspaces'

// Shape of the socket "task_update" payload (backend TaskResponse, see
// backend/app/schemas/_base.py) — a subset of the full Task type.
interface TaskUpdatePayload {
  id: string
  workspace_id: string
  name: string
  description?: string | null
  status: TaskStatus
  type?: string
  progress: number
  error?: string | null
  status_detail?: string | null
  started_at?: string | null
  completed_at?: string | null
  created_at?: string
  updated_at?: string
}

// Module-level singleton (same pattern as useTasks.ts) so every caller shares
// one connection and one transition-tracking map, regardless of remounts.
let socket: Socket | null = null
const lastStatus = new Map<string, string>()

function joinWorkspace(workspaceId: string | undefined) {
  if (socket && workspaceId) {
    socket.emit('join_workspace', { workspace_id: workspaceId })
  }
}

export function initTaskSocket() {
  if (socket) return socket

  const workspaceStore = useWorkspaceStore()
  const snackBarStore = useSnackBarStore()
  const { tasks, fetchData } = useTasks()

  socket = io(config.backendUrl, {
    path: '/ws/socket.io',
    // Function form so a reconnect re-reads the current token instead of
    // replaying the one captured at initial connection (oidc-client rotates
    // the stored token; a stale one here would get rejected forever).
    auth: (cb) => cb({ token: localStorage.getItem('access_token') }),
  })

  socket.on('connect', async () => {
    // Resync before joining: any task_update the server emits after we join
    // arrives over the socket and is applied on top, so it can never be
    // clobbered by this fetch resolving late.
    // ponytail: a rapid disconnect/reconnect within one fetch round-trip
    // could still land a stale snapshot; self-heals on the next task_update
    // or reconnect. Add a connection-generation guard if this proves flaky.
    await fetchData(false)
    joinWorkspace(workspaceStore.selected)
  })

  socket.on('error', (err: { message?: string }) => {
    console.error('[useTaskSocket] server error:', err?.message ?? err)
  })

  socket.on('task_update', (payload: TaskUpdatePayload) => {
    const previousStatus = lastStatus.get(payload.id)
    lastStatus.set(payload.id, payload.status)

    // The export dialog reports its own progress and errors.
    if (payload.status !== previousStatus && payload.type !== 'TableExportExecutor') {
      if (payload.status === 'completed') {
        snackBarStore.SHOW_SUCCESS(`Task "${payload.name}" completed`)
      } else if (payload.status === 'failed') {
        snackBarStore.SHOW_ERROR(
          `Task "${payload.name}" failed${payload.error ? `: ${payload.error.split('\n')[0]}` : ''}`,
        )
      }
    }

    const idx = tasks.value.findIndex((t) => t.id === payload.id)
    if (idx === -1) {
      tasks.value = [...tasks.value, payload as unknown as Task]
    } else {
      tasks.value = tasks.value.map((t) =>
        t.id === payload.id ? { ...t, ...payload } : t,
      ) as Task[]
    }
  })

  // Follow workspace switches without a full reconnect.
  watch(
    () => workspaceStore.selected,
    (workspaceId) => joinWorkspace(workspaceId),
  )

  return socket
}
